import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { practiceAreas } from '../../mocks/practice-areas';
import { attorneys } from '../../mocks/attorneys';

const HomePage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const staggerContainer = {
    initial: {},
    whileInView: { transition: { staggerChildren: 0.1 } },
    viewport: { once: true },
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/cc0b449f66494ba6defd7022b369ad06.jpg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/92 via-[#0A1628]/70 to-[#0A1628]/40"></div>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-7"
            >
              <div className="w-24 h-0.5 bg-gold-500 mb-8"></div>
              <h1 className="font-serif text-5xl lg:text-7xl text-white leading-tight mb-6">
                Hukukta<br />
                Üstün Temsil,<br />
                <span className="text-gold-500">Kalıcı Güven</span>
              </h1>
              <p className="font-sans text-lg text-white/90 max-w-xl mb-10 leading-relaxed">
                Kurumsal, ticari ve uluslararası hukuki meselelerde stratejik çözümler sunan köklü hukuk bürosu
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="px-8 py-4 bg-gold-500 text-white font-sans text-base font-medium rounded-md hover:bg-gold-600 transition-all inline-flex items-center space-x-2 whitespace-nowrap cursor-pointer"
                >
                  <span>Danışmanlık Talep Edin</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/practice-areas"
                  className="px-8 py-4 border-2 border-white text-white font-sans text-base font-medium rounded-md hover:bg-white hover:text-navy-900 transition-all whitespace-nowrap cursor-pointer"
                >
                  Uzmanlık Alanlarını Görüntüle
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="lg:col-span-5"
            >
              <div className="bg-navy-900/40 backdrop-blur-sm border border-gold-500/30 rounded-lg p-8 lg:p-12">
                <div className="space-y-10">
                  <div className="text-center">
                    <div className="text-6xl font-serif text-gold-500 mb-2">25+</div>
                    <div className="text-sm font-sans uppercase tracking-wider text-white/80">Yıllık Deneyim</div>
                  </div>
                  <div className="text-center">
                    <div className="text-6xl font-serif text-gold-500 mb-2">500+</div>
                    <div className="text-sm font-sans uppercase tracking-wider text-white/80">Çözülen Dava</div>
                  </div>
                  <div className="text-center">
                    <div className="text-6xl font-serif text-gold-500 mb-2">15</div>
                    <div className="text-sm font-sans uppercase tracking-wider text-white/80">Uzmanlık Alanı</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <div className="inline-block px-6 py-2 border border-navy-900 rounded-full mb-6">
              <span className="text-xs font-sans uppercase tracking-widest text-navy-900">HUKUK BÜROMUZ</span>
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl text-navy-900 mb-6">
              Dürüstlük Üzerine Kurulu,<br />Mükemmellik ile Yönlendirilen
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-12"
          >
            <motion.div variants={fadeInUp} className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <i className="ri-scales-3-line text-5xl text-gold-500"></i>
              </div>
              <h3 className="font-sans text-xl font-semibold text-navy-900 mb-4">Vizyonumuz</h3>
              <p className="font-sans text-base text-gray-600 leading-relaxed">
                Türkiye'nin en saygın hukuk bürolarından biri olarak, ulusal ve uluslararası müvekkillerimize en yüksek kalitede hukuki hizmet sunmayı hedefliyoruz. Etik değerlere bağlılığımız ve mükemmellik arayışımız ile sektörde öncü olmayı sürdürüyoruz.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <i className="ri-lightbulb-line text-5xl text-gold-500"></i>
              </div>
              <h3 className="font-sans text-xl font-semibold text-navy-900 mb-4">Hukuki Felsefemiz</h3>
              <p className="font-sans text-base text-gray-600 leading-relaxed">
                Her müvekkilimizin benzersiz ihtiyaçları olduğuna inanıyoruz. Stratejik düşünce, detaylı analiz ve yaratıcı çözümlerle müvekkillerimizin hedeflerine ulaşmalarını sağlıyoruz. Proaktif yaklaşımımızla riskleri önceden tespit eder ve önleriz.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <i className="ri-team-line text-5xl text-gold-500"></i>
              </div>
              <h3 className="font-sans text-xl font-semibold text-navy-900 mb-4">Müvekkil Yaklaşımımız</h3>
              <p className="font-sans text-base text-gray-600 leading-relaxed">
                Müvekkillerimizle uzun vadeli ilişkiler kurmayı önceliklendiriyoruz. Şeffaf iletişim, güvenilirlik ve sonuç odaklı çalışma prensiplerimizle müvekkillerimizin güvenini kazanıyor ve koruyoruz. Başarınız bizim başarımızdır.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Practice Areas */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="font-serif text-4xl lg:text-5xl text-navy-900 mb-4">Uzmanlık Alanları</h2>
            <p className="font-sans text-lg text-gray-600 max-w-2xl mx-auto">
              Geniş hukuki uzmanlık yelpazesi ile müvekkillerimize kapsamlı hizmet sunuyoruz
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {practiceAreas.slice(0, 6).map((area) => (
              <motion.div key={area.id} variants={fadeInUp}>
                <Link
                  to={`/practice-areas/${area.slug}`}
                  className="group block bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={area.image}
                      alt={area.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 via-navy-900/50 to-transparent"></div>
                    <div className="absolute top-0 left-0 w-full h-1 bg-gold-500"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="w-12 h-12 flex items-center justify-center bg-gold-500 rounded-md mb-4">
                        <i className={`${area.icon} text-2xl text-white`}></i>
                      </div>
                      <h3 className="font-sans text-xl font-semibold text-white mb-2">{area.title}</h3>
                      <p className="font-sans text-sm text-white/80 line-clamp-2">{area.shortDescription}</p>
                      <div className="mt-4 inline-flex items-center text-gold-500 text-sm font-medium">
                        <span>Daha Fazla Bilgi</span>
                        <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform"></i>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          <motion.div {...fadeInUp} className="text-center mt-12">
            <Link
              to="/practice-areas"
              className="inline-flex items-center px-8 py-4 bg-navy-900 text-white font-sans text-base font-medium rounded-md hover:bg-navy-800 transition-all whitespace-nowrap cursor-pointer"
            >
              <span>Tüm Uzmanlık Alanlarını Görüntüle</span>
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Attorneys */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row items-end justify-between mb-16">
            <motion.div {...fadeInUp}>
              <h2 className="font-serif text-4xl lg:text-5xl text-navy-900 mb-4">Hukuk Ekibimiz</h2>
              <p className="font-sans text-lg text-gray-600 max-w-xl">
                Deneyimli ve uzman avukatlarımız, müvekkillerimize en yüksek kalitede hukuki hizmet sunmaktadır
              </p>
            </motion.div>
          </div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {attorneys.map((attorney) => (
              <motion.div key={attorney.id} variants={fadeInUp}>
                <Link
                  to={`/attorney-profile/${attorney.id}`}
                  className="group block cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg border border-gold-500/30 mb-4">
                    <img
                      src={attorney.image}
                      alt={attorney.name}
                      className="w-full h-96 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="font-sans text-xl font-semibold text-navy-900 mb-1">{attorney.name}</h3>
                    <p className="font-sans text-sm text-gray-600 mb-3">{attorney.title}</p>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-gold-500 mr-2 mt-0.5"></i>
                        <span>{attorney.barMembership}</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-gold-500 mr-2 mt-0.5"></i>
                        <span>{attorney.experience}</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-gold-500 mr-2 mt-0.5"></i>
                        <span>{attorney.specialization}</span>
                      </li>
                    </ul>
                    <div className="mt-4 inline-flex items-center text-gold-500 text-sm font-medium group-hover:translate-x-2 transition-transform">
                      <span>Profili Görüntüle</span>
                      <i className="ri-arrow-right-line ml-2"></i>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-24 bg-navy-900">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-serif text-3xl lg:text-4xl text-white mb-8">
              [ Müvekkil Güveni ve Takdir ]
            </h2>
            <div className="inline-flex items-center px-6 py-2 bg-gold-500 rounded-full mb-8">
              <i className="ri-star-fill text-white mr-2"></i>
              <span className="text-sm font-sans font-medium text-white">5.0 Müvekkil Değerlendirmesi</span>
            </div>
          </motion.div>

          <motion.div {...fadeInUp} className="text-center">
            <div className="text-6xl text-gold-500 mb-6">"</div>
            <p className="font-serif text-2xl lg:text-3xl text-white leading-relaxed mb-8 max-w-3xl mx-auto">
              FP Hukuk ve Danışmanlık, şirketimizin karmaşık uluslararası birleşme sürecinde olağanüstü profesyonellik ve uzmanlık sergiledi. Stratejik yaklaşımları ve detaylı hukuki analizleri sayesinde işlemimizi başarıyla tamamladık.
            </p>
            <div className="flex items-center justify-center space-x-4">
              <div className="w-16 h-16 rounded-full bg-gold-500 flex items-center justify-center">
                <span className="text-2xl font-serif text-white">AK</span>
              </div>
              <div className="text-left">
                <div className="font-sans text-lg font-semibold text-white">Ahmet Kaya</div>
                <div className="font-sans text-sm text-white/70">CEO, TechVenture International</div>
              </div>
            </div>
          </motion.div>

          <motion.div {...fadeInUp} className="flex items-center justify-center space-x-3 mt-12">
            <button className="w-12 h-12 flex items-center justify-center border-2 border-white/30 rounded-md hover:border-gold-500 hover:text-gold-500 text-white transition-colors cursor-pointer" aria-label="Previous">
              <i className="ri-arrow-left-line"></i>
            </button>
            <button className="w-12 h-12 flex items-center justify-center bg-gold-500 rounded-md hover:bg-gold-600 text-white transition-colors cursor-pointer" aria-label="Next">
              <i className="ri-arrow-right-line"></i>
            </button>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-5 relative h-96 lg:h-auto">
            <img
              src="https://readdy.ai/api/search-image?query=elegant%20law%20office%20interior%20with%20leather%20bound%20books%20on%20mahogany%20bookshelf%20sophisticated%20legal%20library%20warm%20ambient%20lighting%20professional%20attorney%20workspace%20premium%20office%20photography&width=800&height=800&seq=cta1&orientation=squarish"
              alt="Law Office"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/20"></div>
            <div className="absolute bottom-12 left-12">
              <div className="text-xs font-sans uppercase tracking-widest text-white/80 mb-4">DANIŞMANLIK TALEBİ</div>
              <div className="font-serif text-white leading-tight">
                <div className="text-5xl mb-2">Uzman</div>
                <div className="text-6xl mb-2">Hukuki</div>
                <div className="text-7xl">Danışmanlık</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 bg-white flex items-center justify-center py-20 px-6 lg:px-16">
            <motion.div {...fadeInUp} className="max-w-xl text-center">
              <h2 className="font-serif text-4xl lg:text-5xl text-navy-900 mb-6 leading-tight">
                Hukuki Meselenizi<br />Görüşmeye Hazır mısınız?
              </h2>
              <p className="font-sans text-base text-gray-600 leading-relaxed mb-8">
                Deneyimli avukatlarımız, karmaşık hukuki zorluklar için stratejik danışmanlık sağlar. Özel ihtiyaçlarınızı görüşmek için gizli bir danışma randevusu planlayın.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center px-10 py-5 bg-navy-900 text-white font-sans text-base font-medium rounded-md hover:bg-navy-800 transition-all whitespace-nowrap cursor-pointer"
              >
                <span>Danışmanlık Talep Edin</span>
                <i className="ri-arrow-right-line ml-3"></i>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;